import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from 'services/api';
import type {
  ViagemControle,
  ViagemBilling,
} from 'pages/viagens-controle/types';
import type { EstadiaAccess } from './access';
import { adaptState, EstadiaCommand, Justification } from './live-data';
import type { DemoState } from './model';
import EstadiaWorkspace from './workspace';
import WorkspaceSkeleton from './workspace-skeleton';

type Paginated<T> = { data: T[]; meta: { lastPage: number; total: number } };
async function fetchAll<T>(
  url: string,
  params: Record<string, string>,
  signal: AbortSignal
) {
  const rows: T[] = [];
  let page = 1;
  let lastPage = 1;
  do {
    const { data } = await api.get<Paginated<T>>(url, {
      params: { ...params, page, limit: 200 },
      signal,
    });
    if (Number(data.meta.total) > 100000)
      throw new Error(
        'O período excede 100.000 registros. Reduza o período de consulta.'
      );
    rows.push(...data.data);
    lastPage = Number(data.meta.lastPage);
    page += 1;
  } while (page <= lastPage);
  return rows;
}
function message(error: unknown) {
  const response = (
    error as {
      response?: {
        status?: number;
        data?: { message?: string; errors?: { message: string }[] };
      };
    }
  )?.response;
  if (response?.status === 401)
    return 'Sua sessão expirou. Entre novamente para consultar os dados.';
  if (response?.status === 403)
    return 'Seu usuário não possui permissão para esta operação.';
  return (
    response?.data?.message ||
    response?.data?.errors?.[0]?.message ||
    (error instanceof Error ? error.message : 'Não foi possível acessar a API.')
  );
}

export default function ConnectedWorkspace({
  access,
  excludeApprovedBilled = false,
}: {
  access: EstadiaAccess;
  excludeApprovedBilled?: boolean;
}) {
  const [state, setState] = useState<DemoState>();
  const [stateScope, setStateScope] = useState(excludeApprovedBilled);
  const [positionAt, setPositionAt] = useState(new Date().toISOString());
  const [options, setOptions] = useState<{
    trip: Justification[];
    billing: Justification[];
  }>({ trip: [], billing: [] });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const active = useRef<AbortController>();
  const mutation = useRef(false);
  const requestedRange = useRef<{ from: string; to: string }>();
  const [rangeVersion, setRangeVersion] = useState(0);
  const onRangeChange = useCallback((from: string, to: string) => {
    const key = `${from}:${to}`;
    if (key === `${requestedRange.current?.from}:${requestedRange.current?.to}`)
      return;
    requestedRange.current = { from, to };
    setRangeVersion((value) => value + 1);
  }, []);
  const load = useCallback(async () => {
    active.current?.abort();
    const controller = new AbortController();
    active.current = controller;
    setLoading(true);
    setError('');
    const to = new Date();
    const from = new Date(to.getFullYear(), to.getMonth() - 11, 1);
    const day = (date: Date) =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    try {
      const scope: Record<string, string> = excludeApprovedBilled
        ? { excludeApprovedBilled: 'true' }
        : {};
      const windows = [{ from: day(from), to: day(to) }];
      const requested = requestedRange.current;
      if (requested && (requested.from < day(from) || requested.to > day(to))) {
        let start = new Date(`${requested.from}T12:00:00`);
        const end = new Date(`${requested.to}T12:00:00`);
        if ((end.getTime() - start.getTime()) / 86400000 > 3660)
          throw new Error('Selecione um período de até 10 anos.');
        while (start <= end) {
          const windowEnd = new Date(
            Math.min(end.getTime(), start.getTime() + 365 * 86400000)
          );
          windows.push({ from: day(start), to: day(windowEnd) });
          start = new Date(windowEnd.getTime() + 86400000);
        }
      }
      const [tripWindows, invoices, tripOptions, billingOptions] =
        await Promise.all([
          Promise.all([
            ...windows.map((window) =>
              fetchAll<ViagemControle>(
                '/viagens-controle/monitoring',
                { ...window, ...scope },
                controller.signal
              )
            ),
            fetchAll<ViagemControle>(
              '/viagens-controle/monitoring',
              { onlyActive: 'true', ...scope },
              controller.signal
            ),
          ]),
          access.viewInvoices
            ? fetchAll<ViagemBilling>(
                '/viagens-controle/billings',
                {},
                controller.signal
              )
            : Promise.resolve([]),
          api.get<Justification[]>('/viagens-controle/justifications-options', {
            params: { type: 'estadia' },
            signal: controller.signal,
          }),
          api.get<Justification[]>('/viagens-controle/justifications-options', {
            params: { type: 'fatura' },
            signal: controller.signal,
          }),
        ]);
      if (controller.signal.aborted) return;
      const trips = Array.from(
        new Map(tripWindows.flat().map((trip) => [trip.id, trip])).values()
      );
      setStateScope(excludeApprovedBilled);
      setState(adaptState(trips, invoices));
      setOptions({ trip: tripOptions.data, billing: billingOptions.data });
      setPositionAt(new Date().toISOString());
    } catch (failure) {
      if (!controller.signal.aborted) {
        setError(message(failure));
        throw failure;
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [access.viewInvoices, excludeApprovedBilled]);
  useEffect(() => {
    load().catch(() => undefined);
    return () => active.current?.abort();
  }, [load, rangeVersion]);
  const execute = async (command: EstadiaCommand) => {
    if (mutation.current) throw new Error('Aguarde a operação em andamento.');
    mutation.current = true;
    setSaving(true);
    try {
      if (command.type === 'validate')
        await api.post(`/viagens-controle/${command.id}/approve`);
      if (command.type === 'rejectTrip')
        await api.post(`/viagens-controle/${command.id}/reject`, {
          comment: command.reason,
          justificationId: command.justificationId,
        });
      if (command.type === 'createInvoice')
        await api.post('/viagens-controle/billings', {
          viagemIds: command.ids.map(Number),
          totalValue:
            (state?.trips
              .filter((trip) => command.ids.includes(trip.id))
              .reduce(
                (total, trip) => total + (trip.assessment?.cents || 0),
                0
              ) || 0) / 100,
        });
      if (command.type === 'approveInvoice')
        await api.post(`/viagens-controle/billings/${command.id}/approve`);
      if (command.type === 'resubmitInvoice')
        await api.post(`/viagens-controle/billings/${command.id}/resubmit`);
      if (command.type === 'dispatch')
        await api.post(`/viagens-controle/billings/${command.id}/dispatch`);
      if (command.type === 'rejectInvoice')
        await api.post(`/viagens-controle/billings/${command.id}/reject`, {
          comment: command.reason,
          justificationId: command.justificationId,
        });
      try {
        await load();
      } catch {
        throw new Error(
          'A operação foi gravada, mas a consulta não atualizou. Atualize os dados antes de continuar.'
        );
      }
    } catch (failure) {
      throw new Error(message(failure));
    } finally {
      mutation.current = false;
      setSaving(false);
    }
  };
  return (
    <>
      {error && (
        <div role="alert" className="stay-callout error">
          {error}{' '}
          <button onClick={() => load().catch(() => undefined)}>
            Tentar novamente
          </button>
        </div>
      )}
      {(!state || stateScope !== excludeApprovedBilled) && loading && <WorkspaceSkeleton />}
      {state && stateScope === excludeApprovedBilled && (
        <EstadiaWorkspace
          showNavigation={false}
          access={
            error
              ? {
                  ...access,
                  validate: false,
                  rejectValidation: false,
                  createInvoice: false,
                  approveInvoice: false,
                  rejectInvoice: false,
                  dispatch: false,
                }
              : access
          }
          initialData={state}
          positionAt={positionAt}
          onCommand={execute}
          justifications={options}
          onRefresh={() => load().catch(() => undefined)}
          busy={loading || saving}
          onRangeChange={onRangeChange}
        />
      )}
    </>
  );
}
